import { useEffect, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { TextAndWordCounter } from 'src/components/TextAndWordCounter';
import { TextCounter } from 'src/components/TextCounter';
import { ASSET_TYPE, isBlogPostAssetType, LANDING_ASSET_TYPE } from 'src/data/asset';
import { canCountCharactersForLanguage, LANGUAGE } from 'src/data/generation';
import { useFreshRef } from 'src/lib/hooks/useFreshRef';
import { countWords } from 'src/lib/string';

import { useStyles } from './useStyles';

export const Counters = ({ className, variationRootElement, assetType, counter, language }) => {
  const styles = useStyles();

  const [selectedText, setSelectedText] = useState('');
  const refVariation = useFreshRef(variationRootElement);

  useEffect(() => {
    const onSelectionChange = () => {
      const rootElement = refVariation?.current;
      if (!rootElement) {
        return;
      }

      const selection = window.getSelection();
      if (selection.rangeCount === 0) {
        setSelectedText('');
        return;
      }

      const range = selection.getRangeAt(0);
      const commonAncestor = range.commonAncestorContainer;

      // User might end up selecting nodes outside of the current Document (for example, some extension ShadowDom or iframe content)
      const isAncestorValid = commonAncestor?.getRootNode() === document;

      // If the current selection is in the "variation" element - update "selected text"
      if (isAncestorValid && rootElement.contains(commonAncestor)) {
        // filter out zwnbsp characters
        const selectedText = selection.toString()?.replace(/\uFEFF/g, '');
        setSelectedText(selectedText);
      } else {
        setSelectedText('');
      }
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [refVariation, setSelectedText]);

  const counterValue = counter?.charsCount || 0;
  const counterStrict = counter?.strict || false;

  // make sure landing copy and all blog assets use chars counter and not word counter
  const displayCharCounterOnly =
    assetType === LANDING_ASSET_TYPE.landingCopy ||
    isBlogPostAssetType(assetType) ||
    canCountCharactersForLanguage(language);

  return displayCharCounterOnly ? (
    <TextCounter
      className={cx(styles.root, className)}
      count={selectedText.length || counterValue}
      maxCount={counter?.maxChars}
      strict={counterStrict}
    />
  ) : (
    <TextAndWordCounter
      className={cx(styles.root, className)}
      charsCount={selectedText.length || counterValue}
      maxChars={counter?.maxChars}
      wordsCount={selectedText ? countWords(selectedText) : counter?.wordsCount}
      maxWords={counter?.maxWords}
      strict={counterStrict && !selectedText}
      displayMaxChars={counterStrict && !selectedText}
    />
  );
};

Counters.propTypes = {
  className: PropTypes.string,
  variationRootElement: PropTypes.instanceOf(Element),
  assetType: PropTypes.oneOf(Object.values(ASSET_TYPE)),
  counter: PropTypes.shape({
    charsCount: PropTypes.number,
    wordsCount: PropTypes.number,
    maxChars: PropTypes.number,
    maxWords: PropTypes.number,
    hint: PropTypes.string,
    strict: PropTypes.bool,
  }),
  language: PropTypes.oneOf(Object.values(LANGUAGE)),
};

Counters.defaultProps = {
  className: null,
  variationRootElement: null,
  assetType: null,
  counter: null,
  language: null,
};
